/**
 * Copy a response, or take it away as a file.
 *
 * Copy puts both rich text and plain text on the clipboard, so pasting into
 * Word or Docs keeps the tables and headings while pasting into a terminal or
 * a code editor gets clean text. Export builds the heavier formats on the
 * server, reusing the same document builders the doc_create tool uses.
 */

import { getHeaders } from './api.js';
import { state } from './state.js';

const FORMATS = [
    { id: 'md', label: 'Markdown', hint: '.md' },
    { id: 'html', label: 'Web page', hint: '.html' },
    { id: 'pdf', label: 'PDF', hint: '.pdf' },
    { id: 'docx', label: 'Word', hint: '.docx' },
    { id: 'txt', label: 'Plain text', hint: '.txt' },
];

const CHECK_SVG = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <polyline points="20 6 9 17 4 12"/>
    </svg>`;

let openMenu = null;

/**
 * Copy the response so it pastes with its formatting intact.
 *
 * The rendered HTML is what the reader sees, so that is what goes on the
 * clipboard as text/html; the message's text goes alongside it as text/plain
 * for anywhere that cannot take markup. Browsers without ClipboardItem get the
 * plain text rather than nothing.
 */
export async function copyMessage(messageEl, button) {
    const contentEl = messageEl?.querySelector('.message-content');
    if (!contentEl) return;
    const html = contentEl.innerHTML;
    const text = contentEl.innerText.trim();
    try {
        if (window.ClipboardItem && navigator.clipboard?.write) {
            await navigator.clipboard.write([
                new window.ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                }),
            ]);
        } else {
            await navigator.clipboard.writeText(text);
        }
        flashCopied(button);
    } catch {
        flashCopied(button, true);
    }
}

/** Swap the icon for a tick, then put it back. */
function flashCopied(button, failed = false) {
    if (!button || button.dataset.flashing === '1') return;
    const original = button.innerHTML;
    button.dataset.flashing = '1';
    button.classList.add(failed ? 'copy-failed' : 'copied');
    button.innerHTML = failed ? '!' : CHECK_SVG;
    button.title = failed ? 'Could not copy' : 'Copied';
    setTimeout(() => {
        button.innerHTML = original;
        button.classList.remove('copied', 'copy-failed');
        button.title = 'Copy this response';
        delete button.dataset.flashing;
    }, 1400);
}

export function closeExportMenu() {
    openMenu?.remove();
    openMenu = null;
    document.removeEventListener('mousedown', onDocMouseDown, true);
    document.removeEventListener('keydown', onDocKeydown, true);
}

function onDocMouseDown(e) {
    if (openMenu && !openMenu.contains(e.target)) closeExportMenu();
}

function onDocKeydown(e) {
    if (e.key === 'Escape') closeExportMenu();
}

/** Open the little format menu above or below the button, whichever fits. */
export function openExportMenu(messageEl, messageId, button) {
    if (openMenu) {
        closeExportMenu();
        return;
    }
    const menu = document.createElement('div');
    menu.className = 'export-menu';
    menu.innerHTML = FORMATS.map(
        (f) =>
            `<button class="export-menu-item" data-fmt="${f.id}">` +
            `<span>${f.label}</span><span class="export-menu-hint">${f.hint}</span></button>`
    ).join('');
    document.body.appendChild(menu);

    const rect = button.getBoundingClientRect();
    const height = menu.offsetHeight;
    // Below by default; above when the footer is near the bottom of the window.
    const below = rect.bottom + height + 8 < window.innerHeight;
    menu.style.top = `${below ? rect.bottom + 6 : rect.top - height - 6}px`;
    menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - menu.offsetWidth - 8))}px`;

    menu.querySelectorAll('.export-menu-item').forEach((item) => {
        item.addEventListener('click', () => {
            const fmt = item.dataset.fmt;
            closeExportMenu();
            askExportKind(messageEl, messageId, fmt);
        });
    });

    openMenu = menu;
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onDocKeydown, true);
}

/**
 * Fetch the file and hand it to the browser.
 *
 * A plain link cannot be used: downloads need the auth header, so the bytes
 * come back through fetch and go out again as an object URL.
 */
async function downloadExport(messageId, fmt) {
    const res = await fetch(
        `/sessions/${state.sessionId}/messages/${messageId}/export?format=${fmt}`,
        { headers: getHeaders(false) }
    );
    if (!res.ok) {
        let detail = `Export failed (${res.status})`;
        try {
            detail = (await res.json()).detail || detail;
        } catch {
            /* the status is enough */
        }
        throw new Error(detail);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const named = /filename="([^"]+)"/.exec(disposition);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = named ? named[1] : `response.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

window.copyMessage = copyMessage;
window.openExportMenu = openExportMenu;


/* ── The choice: take it now, keep it, or both ───────────────────────────── */

/** A small modal shell shared by the dialogs below. */
function modal(title, bodyHtml, actionsHtml) {
    const overlay = document.createElement('div');
    overlay.className = 'msg-editor-overlay small-modal';
    overlay.innerHTML = `
        <div class="msg-editor-panel small">
            <div class="msg-editor-header">
                <span class="msg-editor-title">${title}</span>
                <div class="msg-editor-window-btns">
                    <button class="msg-editor-btn" data-act="close" title="Close" aria-label="Close">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div class="small-modal-body">${bodyHtml}</div>
            <div class="msg-editor-footer">
                <span class="msg-editor-note"></span>
                <div class="msg-editor-actions">${actionsHtml}</div>
            </div>
        </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => {
        if (e.target === overlay) close();
    });
    document.addEventListener('keydown', function esc(e) {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', esc);
        }
    });
    return { overlay, close, note: overlay.querySelector('.msg-editor-note') };
}

function askExportKind(messageEl, messageId, fmt) {
    const { overlay, close, note } = modal(
        `Export as ${fmt.toUpperCase()}`,
        `<p class="small-modal-lead">How would you like it?</p>
         <div class="choice-list">
            <button class="choice" data-kind="download">
                <strong>Download it now</strong>
                <span>Straight to your downloads. Nothing is kept here.</span>
            </button>
            <button class="choice" data-kind="link">
                <strong>Create a download link</strong>
                <span>Kept with this response, with a link in its footer.</span>
            </button>
            <button class="choice" data-kind="both">
                <strong>Both</strong>
                <span>Download it and keep the link.</span>
            </button>
         </div>`,
        ''
    );
    overlay.querySelectorAll('.choice').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const kind = btn.dataset.kind;
            btn.classList.add('busy');
            note.textContent = 'Working…';
            try {
                if (kind === 'download' || kind === 'both') await downloadExport(messageId, fmt);
                if (kind === 'link' || kind === 'both') {
                    const link = await createPermalink(messageId, fmt);
                    addPermalinkToFooter(messageEl, messageId, link);
                }
                close();
            } catch (err) {
                btn.classList.remove('busy');
                note.textContent = err.message;
                note.className = 'msg-editor-note error';
            }
        });
    });
}

async function api(path, options = {}) {
    const res = await fetch(path, {
        ...options,
        headers: { ...getHeaders(!!options.body), ...(options.headers || {}) },
    });
    if (!res.ok) {
        let detail = `Request failed (${res.status})`;
        try {
            detail = (await res.json()).detail || detail;
        } catch {
            /* the status is enough */
        }
        throw new Error(detail);
    }
    return res.json();
}

const createPermalink = (messageId, format) =>
    api(`/sessions/${state.sessionId}/messages/${messageId}/permalink`, {
        method: 'POST',
        body: JSON.stringify({ format }),
    });

/* ── The kept links, on the footer ───────────────────────────────────────── */

const LINK_SVG = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>`;

/** Put the permalink button on a footer, or refresh what it knows. */
export function addPermalinkToFooter(messageEl, messageId, link) {
    const bar = messageEl?.querySelector('.message-actions');
    if (!bar) return;
    const links = JSON.parse(bar.dataset.permalinks || '[]');
    if (link && !links.some((l) => l.media_id === link.media_id)) links.push(link);
    bar.dataset.permalinks = JSON.stringify(links);

    let btn = bar.querySelector('.msg-permalink-btn');
    if (!links.length) {
        btn?.remove();
        return;
    }
    if (!btn) {
        btn = document.createElement('button');
        btn.className = 'message-action-btn msg-permalink-btn ready';
        btn.title = 'Saved download links';
        btn.innerHTML = LINK_SVG;
        const exportBtn = bar.querySelector('.msg-export-btn');
        bar.insertBefore(btn, exportBtn ? exportBtn.nextSibling : null);
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            openPermalinkMenu(messageEl, messageId, btn);
        });
    }
    btn.dataset.count = links.length;
}

function fmtExpiry(iso) {
    const ms = new Date(iso) - new Date();
    if (ms <= 0) return 'expired';
    const hours = Math.round(ms / 3600000);
    if (hours < 48) return `${hours}h left`;
    return `${Math.round(hours / 24)}d left`;
}

function openPermalinkMenu(messageEl, messageId, button) {
    if (openMenu) {
        closeExportMenu();
        return;
    }
    const bar = messageEl.querySelector('.message-actions');
    const links = JSON.parse(bar.dataset.permalinks || '[]');
    const menu = document.createElement('div');
    menu.className = 'export-menu permalink-menu';
    menu.innerHTML = links
        .map((l, i) => {
            const shares = (l.shares || [])
                .map(
                    (sh) =>
                        `<button class="export-menu-item share-row" data-i="${i}" data-token="${sh.token}">
                            <span>Copy share URL</span>
                            <span class="export-menu-hint">${fmtExpiry(sh.expires_at)}</span>
                        </button>`
                )
                .join('');
            return `
                <div class="permalink-group">
                    <div class="permalink-name" title="${l.filename}">${l.filename}</div>
                    <button class="export-menu-item" data-act="download" data-i="${i}">
                        <span>Download</span><span class="export-menu-hint">.${l.format}</span>
                    </button>
                    ${shares}
                    <button class="export-menu-item" data-act="share" data-i="${i}">
                        <span>Create shareable link…</span>
                    </button>
                    <button class="export-menu-item danger" data-act="remove" data-i="${i}">
                        <span>Remove</span>
                    </button>
                </div>`;
        })
        .join('');
    document.body.appendChild(menu);

    const rect = button.getBoundingClientRect();
    const below = rect.bottom + menu.offsetHeight + 8 < window.innerHeight;
    menu.style.top = `${below ? rect.bottom + 6 : Math.max(8, rect.top - menu.offsetHeight - 6)}px`;
    menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - menu.offsetWidth - 8))}px`;

    menu.querySelectorAll('.export-menu-item').forEach((item) => {
        item.addEventListener('click', async () => {
            const links2 = JSON.parse(bar.dataset.permalinks || '[]');
            const link = links2[Number(item.dataset.i)];
            if (!link) return;
            if (item.classList.contains('share-row')) {
                await copyToClipboard(new URL(`/share/${item.dataset.token}`, location.origin).href);
                item.querySelector('span').textContent = 'Copied';
                return;
            }
            const act = item.dataset.act;
            closeExportMenu();
            if (act === 'download') downloadPermalink(link);
            if (act === 'share') askShareLifetime(messageEl, messageId, link);
            if (act === 'remove') confirmRemovePermalink(messageEl, messageId, link);
        });
    });

    openMenu = menu;
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onDocKeydown, true);
}

async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        /* nothing useful to do; the URL is on screen */
    }
}

async function downloadPermalink(link) {
    const res = await fetch(link.url, { headers: getHeaders(false) });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = link.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function askShareLifetime(messageEl, messageId, link) {
    const { overlay, note } = modal(
        'Shareable link',
        `<p class="small-modal-lead">
            Anyone with this link can download <strong>${link.filename}</strong> without
            signing in, until it expires. It cannot be guessed, but it can be forwarded.
         </p>
         <div class="choice-list ttl-list">
            <button class="choice" data-ttl="1h"><strong>1 hour</strong></button>
            <button class="choice" data-ttl="24h"><strong>24 hours</strong></button>
            <button class="choice" data-ttl="7d"><strong>7 days</strong></button>
            <button class="choice" data-ttl="30d"><strong>30 days</strong></button>
         </div>
         <div class="share-result" hidden>
            <input class="share-url" readonly />
            <button class="share-copy">Copy</button>
         </div>`,
        ''
    );
    overlay.querySelectorAll('.choice').forEach((btn) => {
        btn.addEventListener('click', async () => {
            btn.classList.add('busy');
            note.textContent = 'Creating…';
            try {
                const share = await api(
                    `/sessions/${state.sessionId}/messages/${messageId}/permalink/${link.media_id}/share`,
                    { method: 'POST', body: JSON.stringify({ ttl: btn.dataset.ttl }) }
                );
                const full = new URL(share.url, location.origin).href;
                const result = overlay.querySelector('.share-result');
                const input = overlay.querySelector('.share-url');
                input.value = full;
                result.hidden = false;
                overlay.querySelector('.ttl-list').hidden = true;
                note.textContent = `Expires ${new Date(share.expires_at).toLocaleString()}`;
                input.select();
                await copyToClipboard(full);
                overlay.querySelector('.share-copy').addEventListener('click', async () => {
                    await copyToClipboard(full);
                    overlay.querySelector('.share-copy').textContent = 'Copied';
                });
                // Remember it so the footer menu can offer it again.
                const bar = messageEl.querySelector('.message-actions');
                const links = JSON.parse(bar.dataset.permalinks || '[]');
                const mine = links.find((l) => l.media_id === link.media_id);
                if (mine) {
                    mine.shares = mine.shares || [];
                    mine.shares.push({
                        token: share.token,
                        expires_at: share.expires_at,
                        url: share.url,
                    });
                    bar.dataset.permalinks = JSON.stringify(links);
                }
            } catch (err) {
                btn.classList.remove('busy');
                note.textContent = err.message;
                note.className = 'msg-editor-note error';
            }
        });
    });
}

function confirmRemovePermalink(messageEl, messageId, link) {
    const { overlay, close, note } = modal(
        'Remove this download link?',
        `<p class="small-modal-lead">
            <strong>${link.filename}</strong> will be deleted, and any shareable links to it
            will stop working. The response itself is untouched.
         </p>`,
        `<button class="msg-editor-cancel">Cancel</button>
         <button class="msg-editor-save danger-btn">Remove</button>`
    );
    overlay.querySelector('.msg-editor-cancel').addEventListener('click', close);
    overlay.querySelector('.msg-editor-save').addEventListener('click', async (e) => {
        e.target.disabled = true;
        note.textContent = 'Removing…';
        try {
            await api(
                `/sessions/${state.sessionId}/messages/${messageId}/permalink/${link.media_id}`,
                { method: 'DELETE' }
            );
            const bar = messageEl.querySelector('.message-actions');
            const links = JSON.parse(bar.dataset.permalinks || '[]').filter(
                (l) => l.media_id !== link.media_id
            );
            bar.dataset.permalinks = JSON.stringify(links);
            addPermalinkToFooter(messageEl, messageId, null);
            close();
        } catch (err) {
            e.target.disabled = false;
            note.textContent = err.message;
            note.className = 'msg-editor-note error';
        }
    });
}

/** Decorate a freshly loaded transcript with whatever links it already has. */
export async function loadSessionPermalinks() {
    if (!state.sessionId) return;
    let data;
    try {
        data = await api(`/sessions/${state.sessionId}/permalinks`);
    } catch {
        return;
    }
    Object.entries(data.permalinks || {}).forEach(([messageId, links]) => {
        const el = document.querySelector(`.message.assistant[data-message-id="${messageId}"]`);
        if (!el) return;
        const bar = el.querySelector('.message-actions');
        if (!bar) return;
        bar.dataset.permalinks = JSON.stringify(links);
        addPermalinkToFooter(el, messageId, null);
    });
}

window.addPermalinkToFooter = addPermalinkToFooter;
window.loadSessionPermalinks = loadSessionPermalinks;
